import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage, useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ListingImage } from "@/components/ListingImage";
import AddressMap from "@/components/AddressMap";
import {
  ArrowRight, MapPin, Phone, MessageCircle, User, Calendar,
  ChevronLeft, ChevronRight, Loader2, Gauge, Fuel, Settings, Palette
} from "lucide-react";
import { toast } from "sonner";
import BackBar from "@/components/BackBar";

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user, token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imgIdx, setImgIdx] = useState(0);
  const [showChat, setShowChat] = useState(false);
  const [messageText, setMessageText] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios.get(`${API}/vehicles/${id}`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <DashboardLayout title={t("تفاصيل السيارة", "Vehicle Detail")}><div className="glass-card rounded-2xl h-96 skeleton" /></DashboardLayout>;
  if (!data) return <DashboardLayout title={t("غير موجود", "Not Found")}><div className="glass-card rounded-2xl p-12 text-center text-slate-400">{t("السيارة غير موجودة", "Vehicle not found")}</div></DashboardLayout>;

  const images = data.images?.length ? data.images : [null];

  const startChat = async () => {
    if (!user) { navigate("/login"); return; }
    if (!messageText.trim()) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/chat/messages`, {
        recipient_id: data.owner_id,
        content: messageText,
        listing_id: data.vehicle_id,
        listing_type: "vehicle",
        listing_title: `${data.brand} ${data.model} ${data.year}`
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("تم إرسال الرسالة", "Message sent"));
      navigate(`/chat/conversation/${res.data.conversation_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("فشل الإرسال", "Failed to send"));
    } finally { setSending(false); }
  };

  return (
    <DashboardLayout title={`${data.brand} ${data.model} ${data.year}`}>
      <div className="max-w-6xl space-y-6" data-testid="vehicle-detail">
        <BackBar to="/vehicles" label_ar="سوق السيارات" label_en="Vehicles Marketplace"
          crumbs={[{ to: "/dashboard", ar: "الرئيسية", en: "Home" }, { to: "/vehicles", ar: "السيارات", en: "Vehicles" }, { ar: `${data.brand} ${data.model}`, en: `${data.brand} ${data.model}` }]} />

        <div className="glass-card rounded-2xl overflow-hidden">
          <div className="relative h-96 bg-slate-900">
            <ListingImage path={images[imgIdx]} alt={data.title} className="w-full h-full object-cover" />
            {images.length > 1 && (
              <>
                <button onClick={() => setImgIdx((imgIdx - 1 + images.length) % images.length)} className="absolute top-1/2 right-3 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-yellow-500 hover:text-black transition-colors"><ChevronRight size={20} /></button>
                <button onClick={() => setImgIdx((imgIdx + 1) % images.length)} className="absolute top-1/2 left-3 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-yellow-500 hover:text-black transition-colors"><ChevronLeft size={20} /></button>
              </>
            )}
          </div>
          {images.length > 1 && (
            <div className="p-3 flex gap-2 overflow-x-auto">
              {images.map((p, i) => (
                <button key={i} onClick={() => setImgIdx(i)} className={`w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 border-2 ${i === imgIdx ? "border-yellow-500" : "border-transparent"}`}>
                  <ListingImage path={p} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="glass-card rounded-2xl p-6">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <Badge className={`mb-2 ${data.listing_type === "rent" ? "bg-blue-500/20 text-blue-400" : "bg-yellow-500/20 text-yellow-400"}`}>
                    {data.listing_type === "rent" ? t("للإيجار", "For Rent") : t("للبيع", "For Sale")}
                  </Badge>
                  <h1 className="text-2xl font-bold text-white mb-1">{data.brand} {data.model} {data.year}</h1>
                  <div className="flex items-center gap-1 text-slate-400"><MapPin size={14} className="text-yellow-500" /><span>{data.city}, {data.state}</span></div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-yellow-400">${data.price?.toLocaleString()}</div>
                  <div className="text-sm text-slate-400">{data.listing_type === "rent" ? t("يومياً", "per day") : t("سعر البيع", "selling price")}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 my-6">
                {[
                  { i: Calendar, label: t("سنة", "Year"), val: data.year },
                  { i: Gauge, label: t("الميلاج", "Mileage"), val: `${data.mileage?.toLocaleString()} mi` },
                  { i: Settings, label: t("ناقل الحركة", "Trans."), val: data.transmission },
                  { i: Fuel, label: t("الوقود", "Fuel"), val: data.fuel_type }
                ].map((it, i) => (
                  <div key={i} className="bg-slate-900 rounded-xl p-4 text-center">
                    <it.i className="text-yellow-500 mx-auto mb-1" size={20} />
                    <div className="text-white font-bold capitalize text-sm">{it.val}</div>
                    <div className="text-xs text-slate-400">{it.label}</div>
                  </div>
                ))}
              </div>

              <div className="flex gap-3 flex-wrap mb-4">
                <div className="flex items-center gap-2 bg-slate-900 rounded-full px-4 py-2">
                  <Palette className="text-yellow-500" size={16} />
                  <span className="text-slate-300 text-sm">{data.color}</span>
                </div>
                <div className="flex items-center gap-2 bg-slate-900 rounded-full px-4 py-2">
                  <span className="text-slate-300 text-sm capitalize">{data.condition}</span>
                </div>
              </div>

              <div>
                <h3 className="font-bold text-white mb-2">{t("الوصف", "Description")}</h3>
                <p className="text-slate-300 whitespace-pre-wrap leading-relaxed">{data.description}</p>
              </div>
            </div>

            <div className="glass-card rounded-2xl p-6">
              <h3 className="font-bold text-white mb-3 flex items-center gap-2">
                <MapPin className="text-yellow-500" size={18} />
                {t("موقع السيارة", "Vehicle Location")}
              </h3>
              <AddressMap address={`${data.city}, ${data.state}, USA`} title={`${data.brand} ${data.model}`} height="h-64" />
            </div>
          </div>

          <div className="space-y-4">
            <div className="glass-card rounded-2xl p-6 space-y-4">
              <h3 className="font-bold text-white">{t("التواصل مع المالك", "Contact Owner")}</h3>
              <div className="flex items-center gap-3 p-3 bg-slate-900 rounded-xl">
                <div className="w-12 h-12 rounded-full bg-yellow-500 text-black flex items-center justify-center font-bold"><User size={20} /></div>
                <div>
                  <div className="text-white font-semibold">{data.owner_name}</div>
                  <div className="text-xs text-slate-400 flex items-center gap-1"><Calendar size={12} /> {new Date(data.created_at).toLocaleDateString()}</div>
                </div>
              </div>
              <a href={`tel:${data.phone}`} className="block">
                <Button className="w-full btn-primary" data-testid="vcall-owner-btn"><Phone className="ml-2" size={18} /> {data.phone}</Button>
              </a>
              {user?.user_id !== data.owner_id && (
                <Button onClick={() => setShowChat(!showChat)} variant="outline" className="w-full border-yellow-500/40 text-yellow-400 hover:bg-yellow-500/10" data-testid="vopen-chat-btn">
                  <MessageCircle className="ml-2" size={18} /> {t("دردشة خاصة", "Private chat")}
                </Button>
              )}
              {showChat && user?.user_id !== data.owner_id && (
                <div className="space-y-2">
                  <textarea className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-white focus:border-yellow-500 outline-none" rows={3} placeholder={t("اكتب رسالتك...", "Write a message...")} value={messageText} onChange={(e) => setMessageText(e.target.value)} data-testid="vchat-message-input" />
                  <Button disabled={sending || !messageText.trim()} onClick={startChat} className="w-full btn-primary" data-testid="vsend-chat-btn">
                    {sending ? <Loader2 className="animate-spin" size={16} /> : t("إرسال", "Send")}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
